// The field: paints one of three banded colour surfaces filling the THEATER,
// faded only at the coast — wind speed, wave height, or the combined "Limits"
// layer that takes, per pixel, whichever of the two is nearer its operating
// limit (max(wind/windLimit, wave/waveLimit)). The ramp is anchored to the
// vessel's limits — red = "at your limit" — so the map reads as go/no-go, not a
// generic weather chart. Rendered to a <canvas> (which makes the coast fade and
// colour banding trivial) and shown as a MapLibre image layer; stepping the hour
// just re-draws from the in-memory mesh.
//
// It used to be a DISC — clipped to the vessel's one-day steaming reach around the
// site and feathered at that radius. That predates the theater. Once a confirmed
// rectangle is drawn on the map and called the operating area, a circle of colour
// floating inside it is the map contradicting itself: the frame says "this is the
// area" and the paint says "no, this is". The extent now IS the theater, so the
// two agree, the coloured area stops sliding about as the site is dragged, and the
// field no longer needs a vessel with a known cruise speed just to exist.
//
// Coast fade: Open-Meteo's marine wave height is null over land, so mesh.wave marks
// the sea. Both fields fade where that mask thins out — no coastline dataset needed.

import maplibregl from "maplibre-gl";
import type { Map as MlMap } from "maplibre-gl";
import type { FieldKind, FieldMesh } from "../forecast/fieldmesh.js";
import { MERC_MAX_LAT, mercY, latOfMercY } from "./mercator.js";

/**
 * What the field can paint. FieldKind ("wind" | "wave") types the DATA in the
 * mesh and the chart sampling, so it stays two-valued; the renderer's choice of
 * SURFACE is wider — it also offers the combined "limits" view, which reads BOTH
 * variables at once. Kept separate on purpose: widening FieldKind would let
 * "limits" leak into meshSeriesAt(), which has no such array to sample.
 */
export type FieldLayer = FieldKind | "limits";

/** Both operating limits, carried together because the "limits" layer needs both
 *  at once and the wind/wave layers are cheap to hand the pair they ignore. */
export interface FieldLimits {
  windKn: number;
  waveM: number;
}

/** A layer discriminant that reached here unhandled is a bug, not a default —
 *  the defensive fallback that silently paints a plausible surface has been the
 *  actual defect here before, so an unknown layer must fail loudly at the type
 *  level (never) and at runtime. */
function assertNever(x: never): never {
  throw new Error(`unhandled field layer: ${String(x)}`);
}

const SRC = "reachfield";
const LAYER = "reachfield";
const CANVAS_MAX = 512;
const BASE_ALPHA = 200; // 0–255 over open sea

/**
 * Continuous ramp on value/limit — green (comfortable) → red (at limit) → dark
 * (over) — interpolated between the same anchor colours the old bands used.
 *
 * It used to hard-step between seven flat bands, and every band boundary drew a
 * ragged contour line across the water: an edge with the visual authority of a
 * front, marking nothing but our own quantisation (the 0.75/0.9/1.15 cuts were
 * palette choices, not thresholds anyone sails by). Worse, the legend under the
 * map was ALREADY a smooth CSS gradient — the map disagreed with its own colour
 * key. Interpolating dissolves the false contours; the one boundary that means
 * something — the operating limit — is still carried unmistakably by hue, since
 * everything at or past the limit reads in the reds.
 *
 * The anchors sit at the old bands' midpoints, so every colour still means what
 * it always meant. One scale for anything that ever paints this field, so two
 * views of it can never be read as two different measurements.
 */
const RAMP_STOPS: [number, [number, number, number]][] = [
  [0.2, [26, 120, 60]],
  [0.5, [46, 160, 90]],
  [0.675, [166, 217, 106]],
  [0.825, [253, 206, 80]],
  [0.95, [244, 140, 60]],
  [1.075, [214, 47, 39]],
  [1.25, [140, 15, 26]],
];

export function ramp(frac: number): [number, number, number] {
  const first = RAMP_STOPS[0];
  const last = RAMP_STOPS[RAMP_STOPS.length - 1];
  // Callers skip non-finite fractions before painting; if one ever leaks, dark
  // red is the only defensible answer — the old stepped ramp happened to do the
  // same, and "unknown" must never wear the comfortable green.
  if (Number.isNaN(frac)) return last[1];
  if (frac <= first[0]) return first[1];
  if (frac >= last[0]) return last[1];
  for (let i = 1; i < RAMP_STOPS.length; i++) {
    const [f1, c1] = RAMP_STOPS[i];
    if (frac < f1) {
      const [f0, c0] = RAMP_STOPS[i - 1];
      const t = (frac - f0) / (f1 - f0);
      return [
        Math.round(c0[0] + (c1[0] - c0[0]) * t),
        Math.round(c0[1] + (c1[1] - c0[1]) * t),
        Math.round(c0[2] + (c1[2] - c0[2]) * t),
      ];
    }
  }
  return last[1];
}

/** The rectangle to paint — the theater. */
export interface FieldClip {
  latMin: number;
  latMax: number;
  lonMin: number;
  lonMax: number;
}

interface FieldState {
  mesh: FieldMesh;
  layer: FieldLayer;
  limits: FieldLimits;
  hour: number;
  clip: FieldClip | null;
  cache: Map<number, string>; // hour → data URL; cleared on layer/limit change
}
let state: FieldState | null = null;

/**
 * One hour of the field as a PNG data URL, in Mercator space, spanning the
 * mesh's own extent.
 *
 * Exported for the printable report's site map (report/sitemap.ts), which needs
 * exactly this image and must not compute a second one: two renderers for one
 * field would eventually disagree about a colour, and the client would be
 * holding the disagreement beside the screen that produced it.
 */
export function renderFieldHour(
  mesh: FieldMesh,
  layer: FieldLayer,
  limits: FieldLimits,
  t: number,
  clip: FieldClip | null = null,
): string {
  return renderHour(mesh, layer, limits, t, clip);
}

function renderHour(
  mesh: FieldMesh,
  layer: FieldLayer,
  limits: FieldLimits,
  t: number,
  clip: FieldClip | null,
): string {
  // The image is stretched linearly across its quad in MERCATOR space, so the
  // rows must be spaced in Mercator y and converted back to latitude per row —
  // spacing them linearly in latitude painted every feature poleward of its true
  // position, worst at high latitude on a wide mesh (Hudson Bay sat a whole grid
  // step north of its water). And the paintable extent is clamped to Mercator's
  // own edge: the global mesh reaches ±90, which has no Mercator y at all, and a
  // corner pinned at the projection's limit squeezes every interior row north.
  // The clamp here and in coordsOf MUST agree — the texture is placed by those
  // corners, and rendering for a different extent than the quad claims is
  // exactly the misregistration this block exists to remove.
  const latTop = Math.min(mesh.latMax, MERC_MAX_LAT);
  const latBot = Math.max(mesh.latMin, -MERC_MAX_LAT);
  if (latTop <= latBot) return "";
  const wGeo = mesh.lonMax - mesh.lonMin;
  const hGeo = latTop - latBot;
  const maxDim = Math.max(wGeo, hGeo);
  const W = Math.max(64, Math.round((CANVAS_MAX * wGeo) / maxDim));
  const H = Math.max(64, Math.round((CANVAS_MAX * hGeo) / maxDim));
  const yTop = mercY(latTop);
  const ySpan = yTop - mercY(latBot);
  const cv = document.createElement("canvas");
  cv.width = W;
  cv.height = H;
  const ctx = cv.getContext("2d");
  if (ctx === null) return "";
  const img = ctx.createImageData(W, H);
  const d = img.data;

  const windArr = mesh.wind[t];
  const waveArr = mesh.wave[t];
  const wave = waveArr; // sea mask (finite = sea)
  // Grid spacing comes from the MESH bounds — the data's geometry — never from
  // the clamped render extent above, which is a property of the projection.
  const dLon = wGeo / (mesh.nx - 1);
  const dLat = (mesh.latMax - mesh.latMin) / (mesh.ny - 1);
  const Lw = limits.windKn > 0 ? limits.windKn : 1;
  const Lh = limits.waveM > 0 ? limits.waveM : 1;

  // Bilinear over finite grid values; NaN when no finite neighbour.
  const sampleVal = (arr: Float32Array, lon: number, lat: number): number => {
    const gx = (lon - mesh.lonMin) / dLon;
    const gy = (lat - mesh.latMin) / dLat;
    if (gx < 0 || gy < 0 || gx > mesh.nx - 1 || gy > mesh.ny - 1) return NaN;
    const x0 = Math.floor(gx);
    const y0 = Math.floor(gy);
    const x1 = Math.min(x0 + 1, mesh.nx - 1);
    const y1 = Math.min(y0 + 1, mesh.ny - 1);
    const fx = gx - x0;
    const fy = gy - y0;
    const vv = [arr[y0 * mesh.nx + x0], arr[y0 * mesh.nx + x1], arr[y1 * mesh.nx + x0], arr[y1 * mesh.nx + x1]];
    const ww = [(1 - fx) * (1 - fy), fx * (1 - fy), (1 - fx) * fy, fx * fy];
    let s = 0;
    let w = 0;
    for (let i = 0; i < 4; i++) {
      if (Number.isFinite(vv[i])) {
        s += vv[i] * ww[i];
        w += ww[i];
      }
    }
    return w > 0 ? s / w : NaN;
  };
  // Fraction of the surrounding cells that are sea (wave finite), bilinearly — a
  // smooth 0→1 across the coast, so the field feathers out over the shore.
  const sampleSea = (lon: number, lat: number): number => {
    const gx = (lon - mesh.lonMin) / dLon;
    const gy = (lat - mesh.latMin) / dLat;
    if (gx < 0 || gy < 0 || gx > mesh.nx - 1 || gy > mesh.ny - 1) return 0;
    const x0 = Math.floor(gx);
    const y0 = Math.floor(gy);
    const x1 = Math.min(x0 + 1, mesh.nx - 1);
    const y1 = Math.min(y0 + 1, mesh.ny - 1);
    const fx = gx - x0;
    const fy = gy - y0;
    const s = (i: number): number => (Number.isFinite(wave[i]) ? 1 : 0);
    return (
      s(y0 * mesh.nx + x0) * (1 - fx) * (1 - fy) +
      s(y0 * mesh.nx + x1) * fx * (1 - fy) +
      s(y1 * mesh.nx + x0) * (1 - fx) * fy +
      s(y1 * mesh.nx + x1) * fx * fy
    );
  };

  // The value fed to ramp() at one point: v/L for a single variable, and for the
  // "limits" layer the LARGER of the two fractions — the one nearer to breaking.
  // The one-finite-fraction branch below is belt only: the sea mask IS wave
  // finiteness over the same four bilinear corners, so any pixel that survives to
  // be painted has a finite wave sample and the wave fraction always participates
  // in the max. A wind-only paint that could under-warn a wave-bound vessel is
  // unreachable; the branch exists so a future mask change fails soft, not wrong.
  const fracAt = (lon: number, lat: number): number => {
    switch (layer) {
      case "wind": {
        const v = sampleVal(windArr, lon, lat);
        return Number.isFinite(v) ? v / Lw : NaN;
      }
      case "wave": {
        const v = sampleVal(waveArr, lon, lat);
        return Number.isFinite(v) ? v / Lh : NaN;
      }
      case "limits": {
        const vw = sampleVal(windArr, lon, lat);
        const vh = sampleVal(waveArr, lon, lat);
        const fw = Number.isFinite(vw) ? vw / Lw : NaN;
        const fh = Number.isFinite(vh) ? vh / Lh : NaN;
        if (Number.isFinite(fw) && Number.isFinite(fh)) return Math.max(fw, fh);
        return Number.isFinite(fw) ? fw : fh;
      }
      default:
        return assertNever(layer);
    }
  };

  for (let py = 0; py < H; py++) {
    // Row position is linear in Mercator y (how the quad will stretch it), then
    // converted to the latitude that row truly shows before sampling the grid.
    const lat = latOfMercY(yTop - (py / (H - 1)) * ySpan);
    for (let px = 0; px < W; px++) {
      const lon = mesh.lonMin + (px / (W - 1)) * wGeo;
      const idx = (py * W + px) * 4;
      // Paint only inside the theater. The server snaps the requested box OUTWARD
      // to whole grid cells, so the mesh deliberately covers MORE than the
      // theater — that is what guarantees no bare water inside the frame. Painting
      // all of it would spill up to a whole cell (~20 nm at 0.5 deg) past the
      // frame, and then the map has two different edges claiming to be the
      // operating area. Data extent ⊇ theater; painted extent = theater.
      if (
        clip !== null &&
        (lat < clip.latMin || lat > clip.latMax || lon < clip.lonMin || lon > clip.lonMax)
      ) {
        continue;
      }
      const frac = fracAt(lon, lat);
      if (!Number.isFinite(frac)) continue;
      // coast fade: 0 over land, 1 over open sea, ramped across the shore
      const sea = sampleSea(lon, lat);
      const seaA = Math.max(0, Math.min(1, (sea - 0.25) / 0.55));
      if (seaA <= 0) continue;
      const [r, g, b] = ramp(frac);
      // Coast fade only. The theater's own edge is drawn as a frame, so the paint
      // should meet it squarely rather than dissolving short of it and leaving a
      // ring of bare water inside the box.
      const a = BASE_ALPHA * seaA;
      d[idx] = r;
      d[idx + 1] = g;
      d[idx + 2] = b;
      d[idx + 3] = Math.round(a);
    }
  }
  ctx.putImageData(img, 0, 0);
  return cv.toDataURL();
}

function urlForHour(t: number): string {
  if (state === null) return "";
  const cached = state.cache.get(t);
  if (cached !== undefined) return cached;
  const url = renderHour(state.mesh, state.layer, state.limits, t, state.clip);
  state.cache.set(t, url);
  return url;
}

function coordsOf(mesh: FieldMesh): [[number, number], [number, number], [number, number], [number, number]] {
  // Clamped to Mercator's edge, matching renderHour's paintable extent exactly —
  // a corner at lat 90 has no Mercator y, and a quad pinned at the projection
  // limit drags every interior texture row poleward.
  const t = Math.min(mesh.latMax, MERC_MAX_LAT);
  const b = Math.max(mesh.latMin, -MERC_MAX_LAT);
  return [
    [mesh.lonMin, t],
    [mesh.lonMax, t],
    [mesh.lonMax, b],
    [mesh.lonMin, b],
  ];
}

// --- lead-time styling -------------------------------------------------------
// A field five days out is drawn with exactly the same conviction as one valid in
// an hour, which is the map telling a small lie every time you scrub past the
// weekend. The confidence strip under the ruler says where the character changes;
// this makes the map itself say it, so the two surfaces agree.
//
// Done as a paint property rather than baked into the render: the hour images are
// cached, and re-rendering them to mute the colour would throw that cache away on
// every step. Saturation and opacity are free — the GPU already has the texture.
// 96 h, not the intuitive 72, and the number is cited rather than felt: the
// GEFSv12 wave reforecast (Campos et al., Weather and Forecasting 39(11), Nov
// 2024 — 20 years, 98 deep-water buoys) holds Hs correlation above 0.9 through
// day 4 and above 0.8 through day 7, with the loss of predictability
// concentrated in days 4–10. Fading at 72 h told the operator to distrust a day
// the verification says is still excellent — over-warning, but with the cost
// that real, usable planning range LOOKED unusable. Regional skill is better
// still in TDI's water (tropical Hs scatter barely degrades over five days), so
// if anything 96 is conservative. See ~/.claude/plans/seastate-forecast-skill-numbers.md.
// Exported because the scrubber prints "beyond N h" — the label and the fade must
// quote the same number or the map's words and the map's colour disagree.
export const CONFIDENT_H = 96;
const FAR_H = 168;

// CHANNEL DISCIPLINE — three kinds of muting share this map, and each owns
// exactly one visual channel, or none of them can be read:
//
//   SATURATION  = forecast confidence in TIME. Only this fade touches it. It is
//                 uniform across the whole frame at any playhead position, so
//                 spatial comparisons are never confused by it — it shows itself
//                 when SCRUBBING, as the whole field greying together.
//   LIGHTNESS   = relevance in SPACE (the reach veil's wash toward the theme
//                 ground). It varies across the frame, edged by the 3-day ring.
//   THEME       = Day/Dusk/Night re-casts everything at once — calibration, not
//                 information. Both cues above are RELATIVE within the frame, so
//                 they survive any table.
//
// The fade used to also drop raster-opacity, which is a lightness effect — one
// foot in the veil's channel. A far-lead pixel and a beyond-reach pixel became
// the same kind of pale, and "which muting is this" had no answer at a glance.
// Now: grey-but-solid means far future; pale-but-coloured means beyond reach;
// pale grey means both, and honestly so. The desaturation deepens slightly to
// carry alone what two channels carried before.
/** The fade's raw fraction — 0 at CONFIDENT_H, 1 at FAR_H. Exported so the arrow
 *  layer can mute WITH the raster: two surfaces stating different confidence for
 *  the same hour would be the fade's own words-vs-colour bug, one layer over. */
export function leadFadeFrac(mesh: FieldMesh, t: number): number {
  const valid = mesh.times[t];
  if (valid === undefined || !Number.isFinite(valid)) return 0;
  const leadH = (valid - Date.now()) / 3_600_000;
  return Math.max(0, Math.min(1, (leadH - CONFIDENT_H) / (FAR_H - CONFIDENT_H)));
}

function leadStyle(mesh: FieldMesh, t: number): { sat: number } {
  // Muted, never illegible: at the far end the pattern is still readable, it just
  // stops looking like a measurement. Past forecast hours get no fade at all —
  // analysis is the most trustworthy thing on the map.
  return { sat: -0.75 * leadFadeFrac(mesh, t) };
}

function applyLeadStyle(map: MlMap, t: number): void {
  if (state === null || !map.getLayer(LAYER)) return;
  const { sat } = leadStyle(state.mesh, t);
  map.setPaintProperty(LAYER, "raster-saturation", sat);
  map.setPaintProperty(LAYER, "raster-opacity", 1);
}

/**
 * True when this step is past the confident horizon, i.e. exactly when the fade
 * has begun.
 *
 * Deliberately not "is it *visibly* faded": the caller uses this to print
 * "beyond CONFIDENT_H h", which is a claim about lead time, not about how many percent of
 * saturation came off. Thresholding on visibility made the label appear about
 * three hours after the fade actually started, so the map's words and the map's
 * colour disagreed for a few steps.
 */
export function isLeadFaded(mesh: FieldMesh, t: number): boolean {
  return leadStyle(mesh, t).sat < 0;
}

/** Install (or replace) the field for a mesh + layer, showing hour `hourIdx`.
 *  Both limits are carried even for the single-variable layers: the "limits"
 *  layer needs the pair, and passing it uniformly keeps one code path. Building a
 *  fresh empty cache here is what invalidates stale renders when the LAYER changes;
 *  a limit change with the layer unchanged goes through recolorField instead. */
export function setField(
  map: MlMap,
  mesh: FieldMesh,
  layer: FieldLayer,
  limits: FieldLimits,
  hourIdx: number,
  clip: FieldClip | null = null,
): void {
  state = { mesh, layer, limits, hour: hourIdx, clip, cache: new Map() };
  const url = urlForHour(hourIdx);
  const existing = map.getSource(SRC);
  if (existing && "updateImage" in existing) {
    (existing as maplibregl.ImageSource).updateImage({ url, coordinates: coordsOf(mesh) });
  } else {
    map.addSource(SRC, { type: "image", url, coordinates: coordsOf(mesh) });
    // Below the reach veil when it exists (the veil dims THIS raster, so it must
    // sit above it), else below the range rings, else on top. Order of preference
    // matters: veil sits directly under the rings, so inserting before the veil
    // also lands below the rings.
    const beforeId = map.getLayer("reach-veil-fill")
      ? "reach-veil-fill"
      : map.getLayer("vessel-range-line")
        ? "vessel-range-line"
        : undefined;
    map.addLayer(
      {
        id: LAYER,
        type: "raster",
        source: SRC,
        paint: { "raster-opacity": 1, "raster-fade-duration": 0, "raster-resampling": "linear" },
      },
      beforeId,
    );
  }
  applyLeadStyle(map, hourIdx);
}

/** Step to hour `t` (re-uses cached renders). */
export function showFieldHour(map: MlMap, t: number): void {
  if (state === null) return;
  const src = map.getSource(SRC);
  if (!src || !("updateImage" in src)) return;
  state.hour = t;
  (src as maplibregl.ImageSource).updateImage({ url: urlForHour(t) });
  applyLeadStyle(map, t);
}

/** Re-scale the ramp to new operating limits (re-renders; cache is dropped).
 *  Takes the whole pair: for the "limits" layer a change to EITHER limit repaints,
 *  and clearing the cache here is what stops a stale colour surviving that change. */
export function recolorField(map: MlMap, limits: FieldLimits): void {
  if (state === null) return;
  state.limits = limits;
  state.cache.clear();
  showFieldHour(map, state.hour);
}

export function clearField(map: MlMap): void {
  if (map.getLayer(LAYER)) map.removeLayer(LAYER);
  if (map.getSource(SRC)) map.removeSource(SRC);
  state = null;
}
